import MobileDetect from "mobile-detect";

/**
 * ASMT-CAND-19 (TSK-11687) — 응시 진입 차단을 위한 디바이스 판정.
 *
 * 뷰포트 폭이 아니라 UA 로 판정한다. 창을 좁힌 데스크톱은 응시가 가능하므로 차단 대상이 아니고,
 * 반대로 큰 태블릿은 폭만 보면 데스크톱으로 통과해 버린다.
 */
export enum DeviceKind {
  PHONE = "PHONE",
  TABLET = "TABLET",
  DESKTOP = "DESKTOP",
}

/**
 * iPadOS 13+ Safari 는 **기본 설정에서** 데스크톱 Mac 과 똑같은 UA 를 보낸다
 * (`Macintosh; Intel Mac OS X 10_15_7`). "데스크톱 사이트 요청"을 켠 안드로이드 태블릿도 마찬가지다.
 * 그래서 UA 만 보면 이 기기들이 DESKTOP 으로 새어 나가 차단이 통째로 뚫린다.
 *
 * 터치 포인트로 가른다 — macOS 는 트랙패드를 터치스크린으로 보고하지 않아 `maxTouchPoints` 가 0 이고,
 * iPad 는 5 다. 따라서 "Mac UA + 멀티터치"는 사실상 iPad 하나뿐이라 오탐 위험이 없다.
 */
const isDesktopDisguisedTablet = (navigator: Navigator): boolean => {
  if ((navigator.maxTouchPoints ?? 0) <= 1) return false;
  // navigator.platform 은 deprecated 지만 iPadOS 판별에는 여전히 유일하게 신뢰할 수 있는 값이다.
  // 없는 브라우저를 위해 UA 의 'Macintosh' 로 보강한다.
  return /MacIntel|MacPPC|Mac68K/.test(navigator.platform ?? "")
      || /Macintosh/.test(navigator.userAgent);
};

export const getDeviceKind = (): DeviceKind => {
  // SSR/prerender 구간에는 navigator 가 없다. 데스크톱으로 렌더하고 클라이언트 hydration 에서
  // 실제 판정을 하도록 둔다 — 서버에서 차단하면 정적 생성이 깨진다.
  if (typeof window === "undefined" || typeof window.navigator === "undefined") {
    return DeviceKind.DESKTOP;
  }

  const md = new MobileDetect(window.navigator.userAgent);
  if (md.tablet() !== null) return DeviceKind.TABLET;
  if (md.mobile() !== null) return DeviceKind.PHONE;

  // UA 가 데스크톱이라고 말해도 터치 신호가 있으면 태블릿으로 본다(iPadOS Safari 기본 동작).
  if (isDesktopDisguisedTablet(window.navigator)) return DeviceKind.TABLET;

  return DeviceKind.DESKTOP;
};

/** 모바일 + 태블릿(= 응시 불가 디바이스) 여부. */
export const isMobileOrTablet = (): boolean => getDeviceKind() !== DeviceKind.DESKTOP;
